import 'react-dates/initialize'
import dynamic from 'next/dynamic'
import ErrorBoundary from '../../ErrorBoundary'

const DayPickerRangeController = dynamic(
  () => import('react-dates/lib/components/DayPickerRangeController'),
  { ssr: false }
)

const DayPickerSingleDateController = dynamic(
  () => import('react-dates/lib/components/DayPickerSingleDateController'),
  { ssr: false }
)

// Preload function to initialize react-dates components on hover
// This triggers the dynamic imports so they're ready when the calendar opens
let preloadPromise = null
let componentsLoaded = false

export const preloadReactDates = () => {
  // Only preload once to avoid multiple requests
  if (preloadPromise) return preloadPromise

  preloadPromise = Promise.all([
    import('react-dates/lib/components/DayPickerRangeController'),
    import('react-dates/lib/components/DayPickerSingleDateController')
  ])
    .then(() => {
      componentsLoaded = true
    })
    .catch(() => {
      // Reset on error so we can retry
      preloadPromise = null
      componentsLoaded = false
    })

  return preloadPromise
}

// Check if components are already loaded
export const areReactDatesLoaded = () => componentsLoaded
import 'react-dates/lib/css/_datepicker.css'
import IconNext from '/public/icons/icon-calendar-next.svg'
import IconPrevious from '/public/icons/icon-calendar-previous.svg'
import styles from './styles.module.scss'
import { useResponsive } from '../../../../providers/ResponsiveContext'
import {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef
} from 'react'
import { withModifiers } from '../../../helpers/withModifiers'
import DropdownField from '../../DropdownField'
import Radio from '../Radio'
import Button from '../../Button'
import moment from 'moment'
import IconCalendar from '/public/icons/icon-calendar.svg'
import isEmpty from 'lodash-es/isEmpty'
import classNames from 'classnames'
import { useSearchContext } from '../../../../providers/SearchContext'
import { useRouter } from 'next/router'

const Calendar = ({
  setStartDate,
  setEndDate,
  setOffsetDays,
  numberOfMonths,
  variant = [],
  clearable = false,
  yearJump = false,
  setDate,
  withOffset = false,
  blockedDates = false,
  type,
  disabled = false,
  // eslint-disable-next-line no-unused-vars
  date,
  translations,
  minimumNights,
  className,
  children,
  ...props
}) => {
  const { isSmallMobile } = useResponsive()
  const router = useRouter()
  const { offsetable, search } = useSearchContext()

  // Debug mode detection - controlled by URL parameter ?debug=calendar
  // Memoized to avoid repeated URLSearchParams parsing on every render
  const isDebugMode = useMemo(() => {
    return (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('debug') === 'calendar'
    )
  }, [])

  const previousLocaleRef = useRef(null)
  const localeEnforcedRef = useRef(false)

  // Get the correct locale for the calendar - use router.locale
  const calendarLocale = router.locale || 'en'

  // Ensure react-dates uses the correct locale for the current page
  // Use useLayoutEffect to set locale synchronously before render
  useLayoutEffect(() => {
    if (!previousLocaleRef.current) {
      previousLocaleRef.current = moment.locale()
    }
    // Set locale based on current page language
    moment.locale(calendarLocale)
    localeEnforcedRef.current = true

    return () => {
      localeEnforcedRef.current = false
      if (previousLocaleRef.current) {
        moment.locale(previousLocaleRef.current)
      }
    }
  }, [calendarLocale])

  // Keep locale synced with router.locale even if something else changes it
  useEffect(() => {
    if (localeEnforcedRef.current) {
      if (moment.locale() !== calendarLocale) {
        moment.locale(calendarLocale)
      }
    }
  }, [calendarLocale])

  const [focusedInput, setFocusedInput] = useState('startDate')
  const [initialMonth, setInitialMonth] = useState(
    moment().utc().startOf('day')
  )
  const [singleCalendarMonth, setSingleCalendarMonth] = useState(
    moment().utc().startOf('day')
  )

  const [loading, setLoading] = useState(false)
  const [checked, setChecked] = useState(false)
  const [offset, setOffset] = useState(offsetable)
  const calendarWrapperRef = useRef(null)
  const componentsReadyRef = useRef(componentsLoaded)

  // Check if components are already loaded on mount and update ref
  useEffect(() => {
    // If components are already marked as loaded, use that
    if (componentsLoaded) {
      componentsReadyRef.current = true
      return
    }

    // Check if preload promise exists and wait for it
    if (preloadPromise) {
      preloadPromise
        .then(() => {
          componentsReadyRef.current = true
        })
        .catch(() => {
          componentsReadyRef.current = false
        })
    } else {
      // Try to preload now and mark as ready when done
      const checkReady = async () => {
        try {
          await Promise.all([
            import('react-dates/lib/components/DayPickerRangeController'),
            import('react-dates/lib/components/DayPickerSingleDateController')
          ])
          componentsReadyRef.current = true
        } catch {
          componentsReadyRef.current = false
        }
      }
      checkReady()
    }
  }, [])

  // Debug log when blocked dates are loaded
  useEffect(() => {
    if (isDebugMode && blockedDates && blockedDates.length > 0) {
      console.log('[Calendar Debug] Blocked dates loaded:', {
        count: blockedDates.length,
        dates: blockedDates.map((d) => d.format('YYYY-MM-DD')).sort()
      })
    }
  }, [blockedDates, isDebugMode])

  const returnNearestDayUp = useCallback(
    (offset = 0) => {
      let nearestDate
      blockedDates &&
        blockedDates.map((date) => {
          let diff = date.diff(search.startDate, 'days')
          if (diff > offset && !date.isSame(search.startDate, 'day')) {
            if (nearestDate) {
              if (date.diff(nearestDate, 'days') < 0) {
                nearestDate = date
              }
            } else {
              nearestDate = date
            }
          }
        })
      return nearestDate
    },
    [blockedDates, search.startDate]
  )

  const returnNearestDayDown = useCallback(
    (offset = 0) => {
      let nearestDate
      blockedDates &&
        blockedDates.map((date) => {
          let diff = date.diff(search.endDate, 'days')
          if (diff < offset) {
            if (nearestDate) {
              if (date.diff(nearestDate, 'days') > 0) {
                nearestDate = date
              }
            } else {
              nearestDate = date
            }
          }
        })
      return nearestDate
    },
    [blockedDates, search.endDate]
  )

  useEffect(() => {
    // Skip loading state if components are already ready
    if (componentsReadyRef.current) {
      return
    }
    setLoading(true)
    const timer = setTimeout(() => setLoading(false), 300)
    return () => clearTimeout(timer)
  }, [blockedDates])

  const defaultProps = useMemo(
    () => ({
      navPrev: <IconPrevious />,
      navNext: <IconNext />,
      daySize: isSmallMobile ? 30 : 35,
      numberOfMonths: numberOfMonths ? numberOfMonths : isSmallMobile ? 1 : 2
    }),
    [isSmallMobile, numberOfMonths]
  )

  // Removed unused transitionContainerStyle - styles are applied directly via useEffect

  const returnYears = useCallback(() => {
    let years = []
    for (
      let i = Number(moment().utc().format('YYYY'));
      i <= moment().utc().add(2, 'years').format('YYYY');
      i++
    ) {
      years.push(
        <Radio
          variant={['underline']}
          onChange={(e) => {
            const currentMonth = initialMonth.clone()
            const monthDiff = currentMonth.format('M') - 1
            setChecked(Number(e.target.value))
            setDate(Number(e.target.value))
            setInitialMonth(
              moment(e.target.value).utc().add(monthDiff, 'months')
            )
            // Skip loading if components are already ready
            if (!componentsReadyRef.current) {
              setLoading(true)
              const timer = setTimeout(() => setLoading(false), 300)
              return () => clearTimeout(timer)
            }
          }}
          name="years"
          label={i}
          checked={checked === i}
          value={i}
          key={`Radio-${i}`}
        >
          {i}
        </Radio>
      )
    }
    return years
  }, [checked, initialMonth, setDate])

  useEffect(() => {
    if (props.startDate === null) {
      setFocusedInput('startDate')
    }
    if (props.startDate && props.endDate === null) {
      setFocusedInput('endDate')
    }
    if (props.startDate && props.endDate) {
      setFocusedInput('startDate')
    }
  }, [props.startDate, props.endDate])

  useEffect(() => {
    if (!calendarWrapperRef.current) return
    const container = calendarWrapperRef.current.querySelector(
      '[class*="DayPicker_transitionContainer"]'
    )
    if (container) {
      container.style.height = 'auto'
      container.style.minHeight = `${isSmallMobile ? 260 : 320}px`
    }

    // Function to override inline styles on week headers
    const overrideWeekHeaderStyles = () => {
      const weekHeaders = calendarWrapperRef.current.querySelectorAll(
        '[class*="DayPicker_weekHeader"]'
      )
      weekHeaders.forEach((header) => {
        if (header.style.padding) {
          header.style.padding = '0 9px'
        }
        // Override left position for second month (December) to align properly
        // First month is typically around 0-20px, second month is around 272px
        const leftValue = parseInt(header.style.left)
        if (leftValue > 250) {
          header.style.left = '257px'
        }
      })
    }

    // Apply immediately
    overrideWeekHeaderStyles()

    // Use MutationObserver to catch when react-dates applies inline styles
    const observer = new MutationObserver(() => {
      overrideWeekHeaderStyles()
    })

    if (container) {
      observer.observe(container, {
        attributes: true,
        subtree: true,
        attributeFilter: ['style']
      })
    }

    return () => {
      observer.disconnect()
    }
  }, [
    isSmallMobile,
    numberOfMonths,
    loading,
    focusedInput,
    props.startDate,
    props.endDate
  ])

  const handleDatesChange = useCallback(
    (startDate, endDate) => {
      // Debug log when user clicks dates
      if (isDebugMode) {
        console.log('[Calendar Debug] Date clicked:', {
          newStart: startDate?.format('YYYY-MM-DD'),
          newEnd: endDate?.format('YYYY-MM-DD'),
          currentStart: props.startDate?.format('YYYY-MM-DD'),
          currentEnd: props.endDate?.format('YYYY-MM-DD'),
          focusedInput: focusedInput
        })
      }

      // Ensure dates are set to start of day
      const normalizedStartDate = startDate
        ? moment(startDate).utc().startOf('day')
        : null
      const normalizedEndDate = endDate
        ? moment(endDate).utc().startOf('day')
        : null

      // Functionality that focuses start date / end date
      // It will always have first click start date, second click end date

      if (props.startDate === null && props.endDate === null) {
        setStartDate(normalizedStartDate)
        setTimeout(() => setFocusedInput('endDate'), 10)
      }

      if (props.startDate === null && props.endDate) {
        setStartDate(normalizedStartDate)
        setTimeout(() => setFocusedInput('endDate'), 10)
      }

      if (props.startDate && props.endDate === null) {
        setEndDate(normalizedEndDate)
        setTimeout(() => setFocusedInput('startDate'), 10)
      }

      if (props.startDate && props.endDate) {
        setEndDate(null)
        setStartDate(normalizedStartDate)
        setTimeout(() => setFocusedInput('endDate'), 10)
      }

      if (
        (normalizedStartDate &&
          normalizedStartDate?.isBefore(props.startDate, 'day')) ||
        normalizedStartDate?.isAfter(props.endDate, 'day')
      ) {
        setEndDate(null)
        setStartDate(normalizedStartDate)
        setTimeout(() => setFocusedInput('endDate'), 10)
      }
    },
    [
      props.startDate,
      props.endDate,
      setStartDate,
      setEndDate,
      focusedInput,
      isDebugMode
    ]
  )

  const onFocusChange = useCallback((focusedInput) => {
    setFocusedInput(focusedInput)
  }, [])

  // Track if we've logged the debug summary
  const debugLoggedRef = useRef(false)

  const rangeControllerIsOutside = useCallback(
    (day) => {
      const dayStr = day.format('YYYY-MM-DD')
      const sampleDates = [
        '2025-12-05',
        '2025-12-11',
        '2025-12-15',
        '2026-01-25'
      ]
      const isSampleDate = sampleDates.includes(dayStr)

      // Log summary once
      if (isDebugMode && !debugLoggedRef.current) {
        debugLoggedRef.current = true
        console.log('[Calendar Debug] isOutsideRange check:', {
          hasBlockedDates: !!blockedDates,
          blockedDatesLength: blockedDates?.length,
          isEmptyResult: isEmpty(blockedDates),
          blockedDatesType: typeof blockedDates,
          isArray: Array.isArray(blockedDates),
          firstBlockedDate: blockedDates?.[0]?.format?.('YYYY-MM-DD'),
          todayUTC: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
          todayLocal: moment().format('YYYY-MM-DD HH:mm:ss')
        })
      }

      if (blockedDates && !isEmpty(blockedDates)) {
        let BlockedDate = blockedDates.find(
          (item) => item.format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
        )

        // Log sample dates to see why they're blocked/available
        if (isDebugMode && isSampleDate) {
          let result = 'AVAILABLE'
          if (BlockedDate) {
            result = 'BLOCKED (in list)'
          } else if (day.isBefore(moment().utc(), 'day')) {
            result = 'BLOCKED (past)'
          }
          console.log(`[Calendar Debug] Sample date ${dayStr}:`, {
            isInBlockedList: !!BlockedDate,
            isBefore: day.isBefore(moment().utc(), 'day'),
            result
          })
        }

        if (props.startDate && focusedInput === 'endDate') {
          let NextDay = day.clone().subtract(1, 'day')
          let EndBlocked =
            blockedDates &&
            blockedDates.find(
              (item) =>
                item.format('YYYY-MM-DD') === NextDay.format('YYYY-MM-DD')
            )
          let nearestDate = returnNearestDayUp(-1)
          let booked = EndBlocked?.clone()

          if (nearestDate) {
            const isBlocked =
              day?.isAfter(nearestDate, 'day') +
              day?.isSame(booked?.clone().add(1, 'day'), 'day') +
              day?.isBefore(moment().utc(), 'day')

            return isBlocked
          }
        }
        if (
          props.endDate &&
          props.startDate === null &&
          focusedInput === 'startDate'
        ) {
          let nearestDate = returnNearestDayDown()

          if (nearestDate) {
            const isBlocked =
              day.isBefore(nearestDate, 'day') + day.isSame(BlockedDate, 'day')

            return isBlocked
          }
        }
        if (day.isSame(moment().utc())) {
          return 0
        }

        const isBlocked =
          day.isSame(BlockedDate, 'day') + day.isBefore(moment().utc(), 'day')

        return isBlocked
      }

      const isBlocked = day.isBefore(moment().utc(), 'day')

      return isBlocked
    },
    [
      blockedDates,
      props.startDate,
      props.endDate,
      focusedInput,
      returnNearestDayUp,
      returnNearestDayDown,
      isDebugMode
    ]
  )

  const onPrevMonthClick = useCallback(
    (month) => {
      if (setEndDate) {
        setInitialMonth(month)
        setChecked(Number(month.format('YYYY')))
        setDate(Number(month.format('YYYY')))
        // Skip loading if components are already ready
        if (!componentsReadyRef.current) {
          setLoading(true)
          const timer = setTimeout(() => setLoading(false), 300)
          return () => clearTimeout(timer)
        }
      }
    },
    [setEndDate, setDate]
  )

  const onNextMonthClick = useCallback(
    (month) => {
      if (setEndDate) {
        setInitialMonth(month)
        setChecked(Number(month.format('YYYY')))
        setDate(Number(month.format('YYYY')))
        // Skip loading if components are already ready
        if (!componentsReadyRef.current) {
          setLoading(true)
          const timer = setTimeout(() => setLoading(false), 300)
          return () => clearTimeout(timer)
        }
      }
    },
    [setEndDate, setDate]
  )

  useEffect(() => {
    if (type === 'startDate' || type === 'endDate') {
      if (search.startDate) {
        setSingleCalendarMonth(moment(search.startDate).utc().startOf('day'))
        // Skip loading if components are already ready
        if (!componentsReadyRef.current) {
          setLoading(true)
          const timer = setTimeout(() => setLoading(false), 300)
          return () => clearTimeout(timer)
        }
      }

      if (search.endDate) {
        setSingleCalendarMonth(moment(search.endDate).utc().startOf('day'))
        // Skip loading if components are already ready
        if (!componentsReadyRef.current) {
          setLoading(true)
          const timer = setTimeout(() => setLoading(false), 300)
          return () => clearTimeout(timer)
        }
      }
    }
  }, [search.startDate, search.endDate, type])

  const dayPickerOutsideRange = useCallback(
    (day) => {
      const defaultBlocked = day.isBefore(moment().utc(), 'day')

      if (blockedDates && !isEmpty(blockedDates)) {
        if (type === 'startDate') {
          let BlockedDate =
            blockedDates &&
            blockedDates.find(
              (item) => item.format('YYYY-MM-DD') === day.format('YYYY-MM-DD')
            )

          if (search.endDate) {
            // checks so that you can't select a date that overlaps range of blocked dates
            const isBlocked =
              defaultBlocked +
              day?.isSame(BlockedDate, 'day') +
              day?.isSame(search.endDate, 'day') +
              day?.isAfter(moment().utc().add(3, 'years'))

            return isBlocked
          }
          if (day.isSame(moment().utc(), 'day') && !BlockedDate) {
            return 0
          }

          const isBlocked =
            defaultBlocked +
            day?.isSame(BlockedDate, 'day') +
            day?.isAfter(moment().utc().add(3, 'years'))

          return isBlocked
        }

        if (type === 'endDate') {
          let NextDay = day.clone().subtract(1, 'day')
          let BlockedDate =
            blockedDates &&
            blockedDates.find(
              (item) =>
                item.format('YYYY-MM-DD') === NextDay.format('YYYY-MM-DD')
            )

          let booked = BlockedDate?.clone()

          if (search.startDate) {
            // checks so that you can't select a date that overlaps range of blocked dates
            let nearestDate = returnNearestDayUp()

            if (nearestDate) {
              const isBlocked =
                defaultBlocked +
                day?.isSame(booked?.clone().add(1, 'day'), 'day') +
                day?.isSame(search.startDate, 'day') +
                day?.isAfter(moment().utc().add(3, 'years'))

              return isBlocked
            }

            const isBlocked =
              defaultBlocked +
              BlockedDate +
              day?.isSame(search.startDate, 'day') +
              day?.isAfter(moment().utc().add(3, 'years'))

            return isBlocked
          }

          const isBlocked =
            defaultBlocked +
            day?.isSame(booked?.clone().add(1, 'day'), 'day') +
            day?.isAfter(moment().utc().add(3, 'years'))

          return isBlocked
        }
      }

      return defaultBlocked
    },
    [blockedDates, type, search.endDate, search.startDate, returnNearestDayUp]
  )

  // Memoize the calendar key to only update when the month changes, not when blocked dates change
  // This prevents unnecessary re-mounts of the DayPickerRangeController
  const calendarKey = useMemo(
    () => initialMonth.format('YYYY-MM'),
    [initialMonth]
  )

  // Ensure locale is correct right before rendering react-dates components
  // This runs synchronously during render to ensure react-dates uses the page locale
  if (typeof window !== 'undefined') {
    moment.locale(calendarLocale)
  }

  const minimumNightsValue = useMemo(() => {
    return (
      (blockedDates &&
        (returnNearestDayUp() === undefined ||
          (props.startDate &&
            returnNearestDayUp() &&
            returnNearestDayUp().diff(props.startDate, 'days') >
              minimumNights)) &&
        minimumNights) ||
      0
    )
  }, [blockedDates, returnNearestDayUp, props.startDate, minimumNights])

  return (
    <ErrorBoundary fallbackMessage="Unable to load calendar. Please refresh.">
      <div
        className={classNames(
          className,
          withModifiers(
            'calendar',
            [...variant, disabled && 'disabled'],
            styles
          )
        )}
      >
        {setEndDate ? (
          <>
            <div className={styles.calendar__wrapper} ref={calendarWrapperRef}>
              {loading ? (
                <div className={styles.calendar__skeleton}>
                  <div className={styles.calendar__skeletonNav}>
                    <div className={styles.calendar__skeletonMonth}></div>
                  </div>
                  <div className={styles.calendar__skeletonGrid}>
                    {Array.from({ length: 42 }).map((_, index) => (
                      <div
                        key={`skeleton-${index}`}
                        className={styles.calendar__skeletonDay}
                      ></div>
                    ))}
                  </div>
                </div>
              ) : yearJump ? (
                <DayPickerRangeController
                  {...defaultProps}
                  disabled={disabled}
                  onDatesChange={({ startDate, endDate }) => {
                    handleDatesChange(startDate, endDate)
                  }}
                  key={calendarKey}
                  isOutsideRange={(day) => rangeControllerIsOutside(day)}
                  focusedInput={focusedInput}
                  onFocusChange={onFocusChange}
                  minDate={moment().utc()}
                  onPrevMonthClick={(month) => onPrevMonthClick(month)}
                  onNextMonthClick={(month) => onNextMonthClick(month)}
                  initialVisibleMonth={() => initialMonth}
                  maxDate={moment().utc().add(3, 'years')}
                  minimumNights={minimumNightsValue}
                  {...props}
                />
              ) : (
                <div>
                  <DayPickerRangeController
                    {...defaultProps}
                    disabled={disabled}
                    key={calendarKey}
                    onDatesChange={({ startDate, endDate }) => {
                      handleDatesChange(startDate, endDate)
                    }}
                    maxDate={moment().utc().add(3, 'years')}
                    isOutsideRange={(day) => rangeControllerIsOutside(day)}
                    focusedInput={focusedInput}
                    onFocusChange={onFocusChange}
                    minDate={moment().utc()}
                    {...props}
                  />
                </div>
              )}
            </div>

            {withOffset && (
              <div className={styles.calendar__offsets}>
                {Array.from({ length: 4 }).map((currentElement, index) => (
                  <Radio
                    value={index}
                    label={
                      index === 0
                        ? translations?.exactDates[router.locale]
                        : `± ${index} ${
                            !isSmallMobile
                              ? translations?.days[router.locale]
                              : ''
                          }`
                    }
                    onChange={(e) => {
                      setOffset(Number(e.target.value))
                      setOffsetDays(Number(e.target.value))
                    }}
                    name="offset"
                    checked={index === offset}
                    key={`Radio-${currentElement}-${index}`}
                    variant={['inline', 'boxed']}
                  />
                ))}
              </div>
            )}

            {(clearable || yearJump) && (
              <div
                className={classNames(
                  styles.calendar__footer,
                  'calendar-footer'
                )}
              >
                {children}
                {clearable && (
                  <Button
                    type="button"
                    onClick={() => {
                      setStartDate(null)
                      setEndDate(null)
                      setFocusedInput('startDate')
                    }}
                    disabled={
                      props.startDate === null && props.endDate === null
                    }
                    variant={['label', 'gray']}
                    arrow={false}
                  >
                    {isSmallMobile
                      ? translations?.clear[router.locale]
                      : translations?.clearDates[router.locale]}
                  </Button>
                )}

                <div className={styles.calendar__select}>
                  <div className={styles.calendar__label}>
                    {translations.skip[router.locale]}
                  </div>

                  <DropdownField
                    variant={['top', 'dropdown', 'accent', 'p-xs']}
                    footer={false}
                    name="yearChange"
                    width={9.4}
                    disabled={disabled}
                    prependIcon={<IconCalendar />}
                    value={initialMonth.format('YYYY')}
                  >
                    <div>{returnYears()}</div>
                  </DropdownField>
                </div>
              </div>
            )}

            {!clearable && !yearJump && children && (
              <div
                className={classNames(
                  styles.calendar__footer,
                  'calendar-footer'
                )}
              >
                {children}
              </div>
            )}
          </>
        ) : (
          <div
            className={withModifiers(
              'calendar__single',
              [disabled && 'disabled'],
              styles
            )}
          >
            {loading ? (
              <div className={styles.calendar__skeleton}>
                <div className={styles.calendar__skeletonNav}>
                  <div className={styles.calendar__skeletonMonth}></div>
                </div>
                <div className={styles.calendar__skeletonGrid}>
                  {Array.from({ length: 35 }).map((_, index) => (
                    <div
                      key={`skeleton-single-${index}`}
                      className={styles.calendar__skeletonDay}
                    ></div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <DayPickerSingleDateController
                  {...defaultProps}
                  transitionDuration={0}
                  onDateChange={(date) => {
                    setStartDate(date)
                  }}
                  initialVisibleMonth={() => singleCalendarMonth}
                  date={props.startDate}
                  focused={true}
                  isOutsideRange={(day) => dayPickerOutsideRange(day)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}

const withCalendar = (Component) =>
  function Wrapper(props) {
    const [date, setDate] = useState(0)

    return <Component date={date} setDate={setDate} {...props} />
  }

export default withCalendar(Calendar)
